import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ImageGallery from '../components/ImageGallery';

const { FiAward, FiUsers, FiTool, FiHeart } = FiIcons;

const About = () => {
  const storyRef = React.useRef(null);
  const { scrollYProgress } = useScroll({
    target: storyRef,
    offset: ['start end', 'end start']
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ['-20%', '20%']);

  const values = [
    {
      icon: FiAward,
      title: 'Qualität',
      description: 'Höchste Ansprüche an Handwerk und Material'
    },
    {
      icon: FiUsers,
      title: 'Erfahrung',
      description: 'Über 8 Jahre Expertise im Motorradbau'
    },
    {
      icon: FiTool,
      title: 'Präzision',
      description: 'Millimetergenaue Arbeit für perfekte Ergebnisse'
    },
    {
      icon: FiHeart,
      title: 'Leidenschaft',
      description: 'Motorräder sind unsere Leidenschaft, nicht nur unser Beruf'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-900"
    >
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://lima-service.de/Grafiken/12.jpg')` }}
        />
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-6">
              Über <span className="gradient-text">uns</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 max-w-3xl mx-auto">
              Leidenschaft für Motorräder seit 2017
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section ref={storyRef} className="py-20 bg-gray-800 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-8">
                Unsere <span className="gradient-text">Geschichte</span>
              </h2>
              <div className="space-y-6 text-lg text-gray-300">
                <p>
                  Bavaria Custom Bikes wurde 2017 aus der Leidenschaft für Motorräder und dem Wunsch gegründet, individuelle Träume auf zwei Rädern zu verwirklichen. Was als kleine Werkstatt in Tapfheim begann, hat sich zu einem der führenden Anbieter für Motorrad-Umbauten in Bayern entwickelt.
                </p>
                <p>
                  Unser Team besteht aus erfahrenen Mechanikern und Designern, die ihr Handwerk verstehen und mit modernster Technik arbeiten. Jedes Projekt ist für uns einzigartig und wird mit höchster Präzision und Liebe zum Detail umgesetzt.
                </p>
                <p>
                  Von klassischen Harley-Davidson Umbauten bis hin zu modernen Sportbike-Modifikationen - wir verwirklichen Ihre Vision und schaffen Motorräder, die nicht nur fahren, sondern Emotionen wecken.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-w-4 aspect-h-3 rounded-xl overflow-hidden">
                <motion.img 
                  src="https://lima-service.de/Grafiken/Bilder/11.jpg" 
                  alt="Werkstatt" 
                  className="w-full h-full object-cover"
                  style={{ y: imageY }}
                />
              </div>
              <motion.div 
                className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-r from-orange-600 to-orange-500 rounded-xl flex items-center justify-center"
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="text-center text-white">
                  <div className="text-3xl font-bold">8+</div>
                  <div className="text-sm">Jahre</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Unsere <span className="gradient-text">Werte</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Diese Prinzipien leiten uns bei allem, was wir tun
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center group"
              >
                <motion.div 
                  className="w-20 h-20 bg-gradient-to-r from-orange-600 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6"
                  whileHover={{ scale: 1.2, rotate: 15, boxShadow: "0 0 30px rgba(234, 88, 12, 0.6)" }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <SafeIcon icon={value.icon} className="w-10 h-10 text-white" />
                </motion.div>
                <h3 className="text-2xl font-bold text-white mb-4">{value.title}</h3>
                <p className="text-gray-300">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Gallery Carousel Section */}
      <ImageGallery />

      {/* Process / Workflow Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Der Weg zum <span className="gradient-text">Traum-Bike</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Transparenz und Präzision in jedem Schritt
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                iconName: 'MessageCircle',
                title: 'Beratung & Vision',
                description: 'Alles beginnt mit einem Gespräch. Wir analysieren Ihre Wünsche, definieren das Ziel und prüfen die Machbarkeit.'
              },
              {
                iconName: 'PenTool',
                title: 'Planung & Design',
                description: 'Wir entwerfen das Konzept, wählen passende Komponenten aus und planen den technischen Ablauf im Detail.'
              },
              {
                iconName: 'Tool',
                title: 'Handwerk & Umbau',
                description: 'In unserer Meisterwerkstatt wird Ihr Bike mit höchster Präzision, Sorgfalt und Leidenschaft umgebaut.'
              },
              {
                iconName: 'CheckCircle',
                title: 'TÜV & Übergabe',
                description: 'Nach erfolgreicher TÜV-Abnahme und strenger Endkontrolle übergeben wir Ihnen Ihr fahrbereites Unikat.'
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative bg-gray-900/50 rounded-2xl p-8 border border-gray-700 hover:border-orange-500/50 transition-colors group overflow-hidden"
              >
                {/* Step Number Background */}
                <div className="absolute -top-6 -right-4 text-9xl font-black text-gray-800/50 select-none pointer-events-none z-0 opacity-20 group-hover:opacity-40 transition-opacity">
                  {index + 1}
                </div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center mb-6 border border-gray-700 group-hover:border-orange-500/50 group-hover:bg-gray-800/80 transition-all shadow-lg">
                    <SafeIcon name={step.iconName} className="w-8 h-8 text-orange-500 group-hover:scale-110 transition-transform" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-orange-400 transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed text-sm">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default About;